import { EventSourceMessage, EventStreamContentType, fetchEventSource } from '@microsoft/fetch-event-source'
import { useCallback } from 'react'

import { captureException } from '@sentry/nextjs'

import appConfig from '~/config/app'
import authService from '~/services/auth.service'
import { refreshToken } from '~/services/axiosInstance.service'

interface Props {
  queryUrl?: string
}
interface ChatParams {
  chatId: string
  data?: Record<string, any>
  query?: Record<string, string>
  /** Cancels the turn, the 401 retry included. Aborting fires none of the callbacks below. */
  signal?: AbortSignal
  onOpen: (response?: Response) => void
  onMessage: (message: EventSourceMessage) => void
  onClose?: () => void
  onError?: (content: string, error?: Error) => void
}

export const useChatAi = ({ queryUrl = '/chat/stream' }: Props): any => {
  const chat = useCallback(
    async ({ data, signal, onOpen, onMessage, onClose, onError }: ChatParams) => {
      const triggerFetch = ({ retry }: { retry: boolean }): Promise<void> => {
        const controller = new AbortController()
        // Per attempt, so a cancel reaches whichever attempt is live: the 401 retry has its own controller.
        const cancel = (): void => controller.abort()
        signal?.addEventListener('abort', cancel, { once: true })
        let retried: Promise<void> | undefined

        const handleError = (status?: number): void => {
          controller.abort()
          const errorType = status === 401 ? 'unauthorized' : status === 429 ? 'rate' : 'network'
          if (errorType !== 'rate') {
            captureException(new Error(`AI Oracle error ${errorType}`), { tags: { feature: 'ai-oracle' } })
          }
          onError?.(errorType)
        }

        return fetchEventSource(`${appConfig.oracleApiUrl}${queryUrl}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${authService.getAccessToken()}`,
          },
          body: JSON.stringify(data),
          signal: controller.signal,
          openWhenHidden: true,
          // eslint-disable-next-line require-await
          onopen: async (response) => {
            if (response.ok && (response.headers.get('content-type')?.indexOf(EventStreamContentType) ?? -1) >= 0) {
              onOpen?.(response)
              return
            } else if (retry && response.status === 401) {
              await refreshToken()
              if (signal?.aborted) return
              // Same `data`, so the same request_id: the backend sees one turn however many attempts.
              retried = triggerFetch({ retry: false })
              // This attempt must not close cleanly: `onClose` would unlock the input mid-retry.
              controller.abort()
            } else {
              handleError(response.status)
            }
          },
          onmessage: (msg) => {
            onMessage?.(msg)
          },
          onclose: () => {
            // An attempt already ended here (401 hand-off, error, cancel) may still have its body read to
            // the end; only a live attempt closes.
            if (controller.signal.aborted) return
            controller.abort()
            onClose?.()
          },
          onerror: (errorMessage) => {
            handleError()
            throw new Error(errorMessage)
          },
        })
          .then(() => retried)
          .finally(() => signal?.removeEventListener('abort', cancel))
      }
      try {
        await triggerFetch({ retry: true })
      } catch (e) {
        captureException(e, { tags: { feature: 'ai-oracle' } })
      }
    },
    [queryUrl],
  )
  return chat
}
